import { useEffect, useRef, useState, type ReactNode } from 'react'

import { cn } from '../../lib/cn'

/**
 * A minimal popover menu. Hand-rolled rather than pulled from a headless UI
 * library because the shell needs exactly two of them (language, user) and the
 * behaviour that matters — Escape closes, outside click closes, focus returns to
 * the trigger — is a dozen lines.
 */
export function Menu({
  trigger,
  label,
  align = 'end',
  children,
}: {
  trigger: (props: { open: boolean }) => ReactNode
  label: string
  align?: 'start' | 'end'
  children: (close: () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      // Without this the focus ring is left orphaned on a removed node and the
      // next Tab restarts from the top of the document.
      triggerRef.current?.focus()
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((value) => !value)}
        className="rounded-md focus-visible:outline-none"
      >
        {trigger({ open })}
      </button>

      {open ? (
        <div
          role="menu"
          className={cn(
            'absolute top-full z-40 mt-1.5 min-w-[12rem] animate-fade-in rounded-lg border',
            'border-border bg-surface p-1 shadow-popover',
            align === 'end' ? 'end-0' : 'start-0',
          )}
        >
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  )
}

export function MenuItem({
  onSelect,
  selected = false,
  children,
  className,
}: {
  onSelect: () => void
  selected?: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2 rounded px-2 py-1.5 text-start text-sm',
        'text-foreground-muted transition-colors hover:bg-surface-hover hover:text-foreground',
        selected && 'text-accent-text',
        className,
      )}
    >
      {children}
    </button>
  )
}

export function MenuSeparator() {
  return <div role="separator" className="my-1 h-px bg-border" />
}
